import { useEffect, type MouseEvent } from 'react';
import { BeliefsSection } from './BeliefsSection';
import { HowIWorkSection } from './HowIWorkSection';
import { StorySection } from './StorySection';
import { Btn } from '../primitives/Btn';
import { BtnOutline } from '../primitives/BtnOutline';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import {
  ACCENT,
  BORDER,
  FONT_DISPLAY,
  FONT_MONO,
  MUTED,
  SURFACE,
  SURFACE2,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

const GREEN = '#22c55e';

interface AboutPageProps {
  ctaHref: string;
  onCta: (event: MouseEvent<HTMLAnchorElement>) => void;
  onBack: () => void;
}

export function AboutPage({ ctaHref, onCta, onBack }: AboutPageProps) {
  const isMobile = useIsMobile();
  const reduced = usePrefersReducedMotion();

  // Arriving from the home page leaves the viewport mid-document.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  }, [reduced]);

  const proseStyle = {
    color: TEXT_SECONDARY,
    fontSize: 'clamp(1rem,1.15vw,1.08rem)',
    lineHeight: 1.85,
  } as const;

  return (
    <div style={{ minHeight: '100dvh' }}>
      {/* Hero band */}
      <section
        style={{
          padding: isMobile ? '120px 20px 60px' : '160px 64px 100px',
          background: 'rgba(8,8,8,0.88)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: '0 auto',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: isMobile ? 32 : 40,
          }}
        >
          <FadeUp delay={60}>
            <div style={{ position: 'relative' }}>
              <img
                src="/about-photo.jpg"
                alt="Brody Zwiers presenting at Claude Community Canberra"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 4,
                  border: `2px solid ${ACCENT}`,
                  boxShadow: `8px 8px 0 ${ACCENT}`,
                  display: 'block',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: -12,
                  right: -12,
                  background: ACCENT,
                  color: SURFACE2,
                  padding: '6px 14px',
                  borderRadius: 4,
                  fontFamily: FONT_MONO,
                  fontSize: '0.62rem',
                  letterSpacing: '0.1em',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  transform: 'rotate(-3deg)',
                }}
              >
                Canberra, AU
              </div>
            </div>
          </FadeUp>

          <div>
            <FadeUp delay={120}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'rgba(255,107,0,0.08)',
                  border: '1px solid rgba(255,107,0,0.25)',
                  color: ACCENT,
                  fontSize: '0.7rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '8px 18px',
                  borderRadius: 100,
                  marginBottom: 28,
                  fontFamily: FONT_MONO,
                  fontWeight: 500,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: ACCENT,
                    display: 'inline-block',
                    animation: 'pulse-dot 2s ease-in-out infinite',
                  }}
                />
                Content OS, built in Canberra
              </div>
            </FadeUp>

            <FadeUp delay={180}>
              <h1
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: 'clamp(2.6rem,5vw,4.8rem)',
                  fontWeight: 400,
                  lineHeight: 1.02,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  margin: '0 0 8px',
                  color: TEXT_PRIMARY,
                }}
              >
                Brody Zwiers
              </h1>
            </FadeUp>

            <FadeUp delay={240}>
              <h2
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: 'clamp(1.2rem,2.2vw,1.8rem)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  margin: '0 0 20px',
                  color: ACCENT,
                }}
              >
                I build the marketing system a business owner can actually keep up with, so the part
                that needs them shrinks to about two hours a week and the rest just runs.
              </h2>
            </FadeUp>

            <FadeUp delay={300}>
              <p
                style={{
                  color: TEXT_SECONDARY,
                  fontSize: 'clamp(1rem,1.2vw,1.1rem)',
                  lineHeight: 1.75,
                  maxWidth: 520,
                  marginBottom: 32,
                }}
              >
                I&rsquo;m 20, I&rsquo;m based in Canberra, and I build and run Content OS myself. It
                handles content, lead response and reviews for business owners who know their marketing should be
                more consistent than it is and do not want to hire a department to fix it. Everything gets
                built from the client&rsquo;s own sales calls and their own competitors, human-checked
                before it publishes, and the files and history stay theirs whether they stay with me or not.
              </p>
            </FadeUp>

            <FadeUp delay={360}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Btn href={ctaHref} onClick={onCta} style={{ fontSize: '0.95rem', padding: '14px 28px' }}>
                  Get My Free Competitor Scan →
                </Btn>
                <BtnOutline
                  onClick={(e) => {
                    e.preventDefault();
                    onBack();
                  }}
                  style={{ fontSize: '0.85rem', padding: '12px 24px' }}
                >
                  ← Back to Home
                </BtnOutline>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <StorySection />
      <BeliefsSection />
      <HowIWorkSection />

      {/* The Mission */}
      <section
        style={{
          padding: isMobile ? '60px 20px' : '100px 64px',
          background: 'rgba(8,8,8,0.88)',
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <FadeUp>
            <SectionLabel number="04">The Mission</SectionLabel>
            <SectionH2 style={{ marginBottom: 24 }}>Give people back their time.</SectionH2>
          </FadeUp>

          <FadeUp delay={80}>
            <p style={{ ...proseStyle, marginBottom: 24 }}>
              The mission isn&rsquo;t to make money off small businesses. Marketing should be something an
              owner can actually keep up with, not something that needs a department, and the 13 to 18 hours
              a week most of them lose to drafting, resizing, scheduling and chasing is time that was never
              going to scale anyway. People who prioritise time over money are measurably happier than
              people who do the reverse, regardless of how much money they have. I am selling back hours,
              not dollars.
            </p>
          </FadeUp>

          <FadeUp delay={140}>
            <p style={{ ...proseStyle, marginBottom: 32 }}>
              The way the model works is simple: everything I figure out gets documented and given away for
              free, the frameworks, the breakdowns, the day-by-day record of building this thing in public,
              and the only people who pay are the ones who want the system installed inside their business
              directly, with 1:1 or 1:many implementation access. Free content for the people who want to
              do it themselves; paid implementation for the people who want it built once and built right.
            </p>
          </FadeUp>

          <FadeUp delay={200}>
            <div
              style={{
                display: 'inline-block',
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderRadius: 20,
                padding: isMobile ? '24px 20px' : '32px 36px',
              }}
            >
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.65rem',
                  color: ACCENT,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: 12,
                }}
              >
                The Enabler
              </div>
              <p style={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.75, maxWidth: 480, margin: '0 auto' }}>
                The role I&rsquo;m playing is <strong style={{ color: TEXT_PRIMARY }}>The Enabler</strong>,
                not the hero of someone else&rsquo;s story, just the catalyst who provides the spark, the
                system, and the roadmap, and then watches what happens. Whether someone works with me
                directly or just uses the free content, the outcome I&rsquo;m optimizing for is the same:
                they change their situation, they get the hours back, they build something that&rsquo;s
                theirs. The transfer of capability from me to them is the actual deliverable. Everything
                else is just the mechanism.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* The Guarantee */}
      <section
        style={{
          padding: isMobile ? '60px 20px' : '80px 64px',
          background: 'rgba(6,6,6,0.88)',
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <FadeUp>
            <div
              style={{
                background: SURFACE,
                border: '1px solid rgba(34,197,94,0.25)',
                borderRadius: 20,
                padding: isMobile ? '28px 22px' : '36px 40px',
              }}
            >
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.65rem',
                  color: GREEN,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: 14,
                }}
              >
                The Guarantee
              </div>
              <h3
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: 'clamp(1.4rem,2.2vw,1.8rem)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: TEXT_PRIMARY,
                  marginBottom: 14,
                }}
              >
                Live in 14 days, or that month is on me.
              </h3>
              <p
                style={{
                  color: TEXT_SECONDARY,
                  fontSize: 'clamp(0.95rem,1.05vw,1rem)',
                  lineHeight: 1.75,
                  maxWidth: 520,
                  margin: '0 auto',
                }}
              >
                No lock-in contract. Month-to-month, cancel anytime with 30 days notice. Your content
                system live in 14 days, and 20 pieces published every month, guaranteed, or that
                month&rsquo;s retainer is credited. It is a promise about build speed and output volume,
                the two things fully inside my control, not a promise about your results.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section
        style={{
          padding: isMobile ? '80px 20px' : '130px 64px',
          background: 'rgba(6,6,6,0.88)',
          textAlign: 'center',
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeUp>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: '0.7rem',
                color: ACCENT,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 24,
              }}
            >
              Ready?
            </div>
            <SectionH2 style={{ marginBottom: 16 }}>
              If anything in this resonates, start with the scan.
            </SectionH2>
            <p style={{ color: TEXT_SECONDARY, fontSize: '1.1rem', lineHeight: 1.75, marginBottom: 36 }}>
              If you&rsquo;re a business owner who wants the system running, start with the free competitor scan below.
              If you&rsquo;re early in the journey and you want to watch the build happen in real time, the
              free content is at{' '}
              <a
                href="https://www.youtube.com/@Brodyzwierz"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: ACCENT, textDecoration: 'none' }}
              >
                @brody.zwierz
              </a>{' '}
              on every platform, start anywhere, it all connects.
            </p>
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <Btn href={ctaHref} onClick={onCta} style={{ fontSize: '1rem', padding: '16px 36px' }}>
                Get My Free Competitor Scan →
              </Btn>
              <span style={{ fontFamily: FONT_MONO, fontSize: '0.65rem', color: MUTED, letterSpacing: '0.08em' }}>
                FREE. NO OBLIGATION. YOURS TO KEEP EITHER WAY.
              </span>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
